/**
 * A house style: how a report looks, under a name, to be put on another.
 *
 * The part of a layout that is a charter rather than a dossier: the paper,
 * the colours, the typefaces and the size of the text, how tables and charts
 * are drawn, the named text styles. Never what the report says (its framework,
 * its currency) and never how it is cut into pages (its paper size, margins,
 * chapter breaks, numbering), so putting a style on a report changes how it
 * looks and nothing else: no figure, no page count decided by somebody, no
 * heading renumbered.
 *
 * Styles come ready-made (`HOUSE_STYLE_PRESETS`) or saved by somebody in the
 * firm's library (`api/houseStyles.ts`), and are read back as forgivingly as a
 * report is: through `readLayout`, setting by setting.
 *
 * Pure: values in, values out.
 */

import { isJsonObject, type JsonObject, type JsonValue } from '@/domain/json'
import {
  ACCENT_PRESETS,
  DEFAULT_LAYOUT,
  readLayout,
  TEXT_SIZES,
  type Layout,
  type TextStyle,
} from '@/domain/layout'
import type { ReportDocument } from '@/domain/report'
import { DEFAULT_TYPEFACE, isTypefaceId, type TypefaceId } from '@/domain/typeface'

/** The settings of a layout a house style holds. */
export const HOUSE_LOOK_KEYS = [
  'paper',
  'accent',
  'palette',
  'accentHeadings',
  'headingTypeface',
  'textSize',
  'justified',
  'tableHead',
  'tableRule',
  'tableDensity',
  'tableZebra',
  'negativesInRed',
  'chartColours',
  'textStyles',
] as const satisfies readonly (keyof Layout)[]

export type HouseLookKey = (typeof HOUSE_LOOK_KEYS)[number]
export type HouseLook = Pick<Layout, HouseLookKey>

export interface HouseStyle {
  /** The report's text face. Stored beside the layout, as a report stores it. */
  readonly typeface: TypefaceId
  readonly look: HouseLook
}

function pick(layout: Layout): HouseLook {
  return Object.fromEntries(HOUSE_LOOK_KEYS.map((key) => [key, layout[key]])) as HouseLook
}

/**
 * A text style as it travels in a house style: without the imported font it
 * may be drawn in, since that font is the report's and not the next one's.
 * The typeface it names stands in, as it does wherever that font is missing.
 */
function travelling(style: TextStyle): TextStyle {
  const { font: _font, ...rest } = style
  return rest
}

/** A report's own look, as a house style. */
export function houseStyleOf(layout: Layout, typeface: TypefaceId): HouseStyle {
  const look = pick(layout)
  return { typeface, look: { ...look, textStyles: look.textStyles.map(travelling) } }
}

/** A house style read off the library: every setting falls back on its own. */
export function readHouseStyle(stored: JsonValue | undefined): HouseStyle {
  const source = isJsonObject(stored) ? stored : {}
  const typeface = source['typeface']
  const look = source['look']
  return {
    typeface: typeof typeface === 'string' && isTypefaceId(typeface) ? typeface : DEFAULT_TYPEFACE,
    look: pick(readLayout(isJsonObject(look) ? look : {})),
  }
}

/** A house style as the library stores it. */
export function writeHouseStyle(style: HouseStyle): JsonObject {
  return {
    typeface: style.typeface,
    look: {
      ...style.look,
      palette: [...style.look.palette],
      textStyles: style.look.textStyles.map((one) => ({ ...one })),
    },
  }
}

/**
 * A layout wearing a house style.
 *
 * The style's text styles join the report's rather than replace them: a
 * section set in one of the report's own styles keeps it. One of the same id
 * or name is the style's version of it, and takes its place.
 */
export function withHouseStyle(layout: Layout, style: HouseStyle): Layout {
  const incoming = style.look.textStyles
  const kept = layout.textStyles.filter(
    (own) => !incoming.some((one) => one.id === own.id || one.name === own.name),
  )
  return { ...layout, ...style.look, textStyles: [...kept, ...incoming] }
}

/** A whole report wearing a house style: its typeface and its layout. */
export function dressedIn(document: ReportDocument, style: HouseStyle): ReportDocument {
  return { ...document, typeface: style.typeface, layout: withHouseStyle(document.layout, style) }
}

/**
 * Whether two styles look the same, which is how the gallery says which one a
 * report already wears. Text styles left out: a report gathers its own, and a
 * ready-made style names none.
 */
export function sameHouseStyle(one: HouseStyle, other: HouseStyle): boolean {
  if (one.typeface !== other.typeface) return false
  return HOUSE_LOOK_KEYS.every(
    (key) =>
      key === 'textStyles' || JSON.stringify(one.look[key]) === JSON.stringify(other.look[key]),
  )
}

/** What a house style changes on a report: the settings that differ, by key. */
export function changedSettings(layout: Layout, typeface: TypefaceId, style: HouseStyle): number {
  const current = houseStyleOf(layout, typeface)
  const differing = HOUSE_LOOK_KEYS.filter(
    (key) =>
      key !== 'textStyles' && JSON.stringify(current.look[key]) !== JSON.stringify(style.look[key]),
  ).length
  return differing + (current.typeface === style.typeface ? 0 : 1)
}

// ---------------------------------------------------------------------------
// Ready-made
// ---------------------------------------------------------------------------

/** The look a report has before anybody styles it, which every preset starts from. */
const PLAIN: HouseLook = pick(DEFAULT_LAYOUT)

function preset(typeface: TypefaceId, look: Partial<HouseLook>): HouseStyle {
  return { typeface, look: { ...PLAIN, ...look } }
}

/**
 * Styles to start from, each a coherent charter rather than a random set:
 * colours that hold up printed in black and white, faces chosen for their
 * figures, tables that read as statements. Named in the message files under
 * `houseStyle.presets`.
 */
export const HOUSE_STYLE_PRESETS = {
  /** What a report looks like untouched: one dark ink, Inter, ruled across. */
  classic: preset(DEFAULT_TYPEFACE, {}),
  /** A group's annual report: navy, serif titles over a sans text, filled heads. */
  institutional: preset('plex-sans', {
    accent: ACCENT_PRESETS.navy,
    palette: ['#0f6b68', '#8a5a2b', '#6b7a8f'],
    accentHeadings: true,
    headingTypeface: 'source-serif',
    tableHead: 'filled',
    tableZebra: true,
    chartColours: 'palette',
  }),
  /** A book's page: cream paper, a serif throughout, burgundy, tables barely ruled. */
  editorial: preset('source-serif', {
    paper: 'cream',
    accent: ACCENT_PRESETS.burgundy,
    palette: ['#8a5a2b', '#2c5b3f'],
    accentHeadings: true,
    headingTypeface: 'lora',
    tableHead: 'plain',
    tableRule: 'minimal',
    tableDensity: 'roomy',
  }),
  /** Light and open: teal, ragged text, tinted heads and banded rows. */
  modern: preset('inter', {
    accent: ACCENT_PRESETS.teal,
    palette: ['#1c3f6e', '#c2410c', '#7c8a96'],
    headingTypeface: 'plex-sans',
    justified: false,
    tableHead: 'tinted',
    tableDensity: 'roomy',
    tableZebra: true,
    chartColours: 'palette',
  }),
  /** A sustainability statement: forest green, a warm serif for titles, the greens in turn. */
  sustainability: preset('inter', {
    accent: ACCENT_PRESETS.forest,
    palette: ['#6a994e', '#a3b18a', '#8a5a2b'],
    accentHeadings: true,
    headingTypeface: 'lora',
    tableHead: 'tinted',
    chartColours: 'palette',
  }),
  /** For the photocopier: black on white, every line drawn, compact. */
  sober: preset('plex-sans', {
    textSize: TEXT_SIZES.small,
    tableHead: 'plain',
    tableRule: 'all',
    tableDensity: 'compact',
  }),
} as const satisfies Record<string, HouseStyle>

export type HouseStylePreset = keyof typeof HOUSE_STYLE_PRESETS

export const HOUSE_STYLE_PRESET_IDS = Object.keys(
  HOUSE_STYLE_PRESETS,
) as readonly HouseStylePreset[]
